import React, { useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Typography } from '@naf-simba/building-blocks-typography';
import { usePollAnswer } from '../../hooks/usePollAnswer';
import { LoginModal } from '../LoginModal';
import { useMembershipStatus } from '../../hooks/useMembershipStatus';
import { checkUserAccess } from '../shared/userAccessibility.utils';
import ResponsiveImage from '../image/ResponsiveImage';
import { IMAGE_SIZES } from '../image/ResponsiveImage.sizes';
import { DynamicVideoPlayer } from '../video-player/DynamicVideoPlayer';
import { calculateDisplayTotalVotes, calculatePercentage, getVotedPolls, saveVotedPoll } from './Poll.utils';
import {
  CloudinaryImageType,
  CloudinaryVideoType,
  PollOption,
  PollStyleVariant,
  UserAccessibility,
} from './Poll.types';
import * as S from './Poll.styles';

interface PollProps {
  pollId: string;
  questionKey: string;
  title: string;
  disclaimer?: string;
  options: PollOption[];
  image?: CloudinaryImageType;
  video?: CloudinaryVideoType;
  style: PollStyleVariant;
  userAccessibility?: UserAccessibility;
}

const Poll = ({
  pollId,
  questionKey,
  title,
  disclaimer,
  options = [],
  image,
  video,
  style = 'light',
  userAccessibility,
}: PollProps) => {
  const { user, isAuthenticated } = useAuth0();
  const userId = user?.sub;
  const { isMembershipActive } = useMembershipStatus();

  const membershipRole = userAccessibility?.membershipRole;

  const hasAccess = checkUserAccess({
    userAccessibility,
    isAuthenticated,
    isMembershipActive,
  });

  const { submitAnswer, isLoading: isSubmitting } = usePollAnswer();
  const [showModal, setShowModal] = useState(false);

  const totalVotes = options.reduce((sum, opt) => sum + (opt.votes || 0), 0);

  const getInitialState = () => {
    const votedPolls = getVotedPolls(userId);
    const localVote = votedPolls[pollId];

    if (localVote) {
      return {
        selectedOption: localVote.optionId,
        votedOptionId: localVote.optionId,
        isSubmitting: false,
        hasVoted: true,
        isOptimisticVote: false,
      };
    }

    return {
      selectedOption: null,
      votedOptionId: null,
      isSubmitting: false,
      hasVoted: false,
      isOptimisticVote: false,
    };
  };

  const [state, setState] = useState(() => getInitialState());

  const handleOptionClick = async (optionId: string) => {
    if (state.hasVoted || state.isSubmitting || isSubmitting) return;

    if (!hasAccess) {
      setShowModal(true);
      return;
    }

    // Optimistic update - show results immediately
    setState((prev) => ({
      ...prev,
      selectedOption: optionId,
      hasVoted: true,
      votedOptionId: optionId,
      isSubmitting: true,
      isOptimisticVote: true,
    }));

    try {
      await submitAnswer(pollId, questionKey, optionId);
      saveVotedPoll(pollId, optionId, userId);
      setState((prev) => ({ ...prev, isSubmitting: false }));
    } catch {
      // Revert on error
      setState((prev) => ({
        ...prev,
        isSubmitting: false,
        hasVoted: false,
        votedOptionId: null,
        selectedOption: null,
        isOptimisticVote: false,
      }));
    }
  };

  const displayTotalVotes = calculateDisplayTotalVotes(state, totalVotes, options);

  const isForMembersOnly = membershipRole?.toLowerCase() === 'member';
  const loginModalCopy = {
    loginRequired: {
      title: 'Du må være logget inn for å gi ditt svar',
    },
    membersOnly: {
      title: 'Du må være medlem for å gi ditt svar',
      body: 'Melder du deg inn, får du også tilgang til alle medlemsfordelene våre.',
    },
  };

  return (
    <>
      <S.Wrapper $style={style}>
        <S.Container $style={style}>
          <S.Content>
            <S.Title variant="headlineXS">{title}</S.Title>
            {video?.publicId && (
              <S.MediaWrapper>
                <DynamicVideoPlayer video={video} />
              </S.MediaWrapper>
            )}
            {!video?.publicId && image?.publicId && (
              <S.MediaWrapper>
                <ResponsiveImage
                  imageId={image.publicId}
                  altText={image.altText || image.caption || ''}
                  sizes={IMAGE_SIZES.bodyStandard}
                />
              </S.MediaWrapper>
            )}
            <S.OptionsContainer>
              {options.map((option) => {
                const isSelected = state.hasVoted
                  ? state.votedOptionId === option.id
                  : state.selectedOption === option.id;
                const percentage = calculatePercentage(state, option.votes || 0, totalVotes, options, option.id);

                return (
                  <S.OptionButton
                    key={option.id}
                    $isSelected={isSelected}
                    $isVoted={state.hasVoted}
                    onClick={() => handleOptionClick(option.id)}
                    disabled={state.hasVoted || state.isSubmitting}
                  >
                    {state.hasVoted && <S.ProgressBar $percentage={percentage} $isSelected={isSelected} />}
                    <S.OptionText variant={isSelected ? 'titleM' : 'textMRegular'}>{option.text}</S.OptionText>
                    {state.hasVoted && (
                      <S.OptionPercentage variant={isSelected ? 'titleM' : 'textMRegular'}>
                        {percentage} %
                      </S.OptionPercentage>
                    )}
                  </S.OptionButton>
                );
              })}
            </S.OptionsContainer>
            {state.hasVoted && displayTotalVotes > 0 && (
              <S.VoteCount variant="textXSRegular">{displayTotalVotes} har stemt</S.VoteCount>
            )}
            {disclaimer && <Typography variant="textXSItalic">{disclaimer}</Typography>}
          </S.Content>
        </S.Container>
      </S.Wrapper>
      <LoginModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={isForMembersOnly ? loginModalCopy.membersOnly.title : loginModalCopy.loginRequired.title}
        body={isForMembersOnly && loginModalCopy.membersOnly.body}
        showBecomeMemberButton={isForMembersOnly}
      />
    </>
  );
};

export default Poll;
